// Central registry for survey question types. The builder, the taking flow, and
// the results dashboard all derive their behavior from this single source so the
// three stay consistent.
import {
  Circle, CheckSquare, ChevronDown, ToggleLeft,
  Star, AlignLeft, Type, Gauge, Scale, Heading, ListOrdered,
} from 'lucide-react';

// Families:
//  - choice  : answered by selecting option(s); stored as option_id rows
//  - numeric : answered by a number; stored as numeric_value
//  - text    : answered by free text; stored as text_value
//  - ranking : answered by assigning each option a unique rank; stored as one
//              option_id row per option with numeric_value = the assigned rank
//  - section : not answerable (heading / descriptive block)
export const QUESTION_TYPES = [
  { key: 'single_choice', label: 'Single choice', short: 'Choice', family: 'choice', single: true, hasOptions: true, icon: Circle, desc: 'Pick exactly one option' },
  { key: 'multi_choice', label: 'Multiple choice', short: 'Multi', family: 'choice', single: false, hasOptions: true, icon: CheckSquare, desc: 'Pick one or more options' },
  { key: 'dropdown', label: 'Dropdown', short: 'Dropdown', family: 'choice', single: true, hasOptions: true, icon: ChevronDown, desc: 'Pick one from a dropdown' },
  { key: 'yes_no', label: 'Yes / No', short: 'Yes/No', family: 'choice', single: true, hasOptions: true, fixedOptions: true, icon: ToggleLeft, desc: 'A simple yes or no' },
  { key: 'rating', label: 'Rating scale', short: 'Rating', family: 'numeric', icon: Star, desc: 'A 1–N star / number rating' },
  { key: 'likert', label: 'Likert (agreement)', short: 'Likert', family: 'numeric', icon: Scale, desc: 'Strongly disagree → Strongly agree' },
  { key: 'nps', label: 'Net Promoter (0–10)', short: 'NPS', family: 'numeric', icon: Gauge, desc: 'How likely to recommend, 0–10' },
  { key: 'ranking', label: 'Ranking', short: 'Rank', family: 'ranking', hasOptions: true, icon: ListOrdered, desc: 'Rank options 1..N (each rank used once)' },
  { key: 'short_text', label: 'Short text', short: 'Short', family: 'text', icon: Type, desc: 'A single line of free text' },
  { key: 'long_text', label: 'Long text', short: 'Long', family: 'text', icon: AlignLeft, desc: 'A paragraph of free text' },
  { key: 'section', label: 'Section / heading', short: 'Section', family: 'section', icon: Heading, desc: 'A non-question heading or note' },
];

const BY_KEY = QUESTION_TYPES.reduce((acc, t) => { acc[t.key] = t; return acc; }, {});

export const typeMeta = (key) => BY_KEY[key] || BY_KEY.single_choice;
export const isChoice = (key) => typeMeta(key).family === 'choice';
export const isNumeric = (key) => typeMeta(key).family === 'numeric';
export const isText = (key) => typeMeta(key).family === 'text';
export const isRanking = (key) => typeMeta(key).family === 'ranking';
export const isAnswerable = (key) => typeMeta(key).family !== 'section';
// Choice + ranking questions both carry an editable list of options.
export const hasOptions = (key) => !!typeMeta(key).hasOptions;

export const LIKERT_PRESETS = {
  agreement: [
    { value: 1, label: 'Strongly disagree' },
    { value: 2, label: 'Disagree' },
    { value: 3, label: 'Neutral' },
    { value: 4, label: 'Agree' },
    { value: 5, label: 'Strongly agree' },
  ],
  satisfaction: [
    { value: 1, label: 'Very dissatisfied' },
    { value: 2, label: 'Dissatisfied' },
    { value: 3, label: 'Neutral' },
    { value: 4, label: 'Satisfied' },
    { value: 5, label: 'Very satisfied' },
  ],
};

// Default config_json for a freshly added question of each type.
export const defaultConfig = (key) => {
  switch (key) {
    case 'rating': return { scale_min: 1, scale_max: 5, icon: 'star', min_label: '', max_label: '' };
    case 'likert': return { points: LIKERT_PRESETS.agreement };
    case 'nps': return {};
    case 'multi_choice': return { min_select: 0, max_select: null };
    case 'ranking': return { require_all: true };
    case 'short_text': return { placeholder: '', max_length: 255 };
    case 'long_text': return { placeholder: '', max_length: 2000 };
    default: return {};
  }
};

// Default options for a freshly added choice / ranking question.
export const defaultOptions = (key) => {
  if (key === 'yes_no') {
    return [
      { option_text: 'Yes', option_value: '1' },
      { option_text: 'No', option_value: '0' },
    ];
  }
  if (hasOptions(key)) {
    return [
      { option_text: '', option_value: null },
      { option_text: '', option_value: null },
    ];
  }
  return [];
};

// A blank question object used by the builder.
export const blankQuestion = (key = 'single_choice') => ({
  id: null,
  question_text: '',
  help_text: '',
  question_type: key,
  is_required: false,
  config: defaultConfig(key),
  options: defaultOptions(key),
});

// The empty answer slot the taking UI keeps per question.
// ranks maps an option id -> its assigned position (1..N) for ranking questions.
export const blankAnswer = () => ({ option_ids: [], value: null, text: '', ranks: {} });

// How many ranks have been assigned in a ranking answer slot.
export const rankCount = (ans) => (ans && ans.ranks ? Object.keys(ans.ranks).length : 0);

// Has this answer slot been filled for a given question type?
// For ranking, "filled" means at least one option ranked; when require_all is set
// (the default), the taking UI treats a required ranking as filled only once every
// option has a position — the caller passes the question so we can honour that.
export const answerIsEmpty = (questionType, ans, question) => {
  if (!ans) return true;
  if (isChoice(questionType)) return !(ans.option_ids && ans.option_ids.length);
  if (isNumeric(questionType)) return ans.value === null || ans.value === undefined || ans.value === '';
  if (isText(questionType)) return !ans.text || !String(ans.text).trim();
  if (isRanking(questionType)) {
    const assigned = rankCount(ans);
    if (assigned === 0) return true;
    const requireAll = question?.config?.require_all !== false;
    const optionCount = (question?.options || []).length;
    if (requireAll && optionCount) return assigned < optionCount;
    return false;
  }
  return true; // section
};

// Rating scale -> array of point values [min..max]
export const ratingPoints = (config) => {
  const min = config?.scale_min ?? 1;
  const max = config?.scale_max ?? 5;
  const out = [];
  for (let i = min; i <= max; i++) out.push(i);
  return out;
};
